import { useEffect, useMemo, useState } from "react";
import {
  fetchAllLiveAndRedesignsByMonth,
  fetchBillingDataByMonth,
  fetchLabWiseCasesByMonth,
  fetchLabWiseRedesignsByMonth,
  fetchMarkedUnitsExport,
  fetchDailyRedesigns,
  fetchLiveProductivity,
  fetchRdPercentage,
  type RdPercentageReport,
  type LiveProductivityReport,
} from "../../store/superAdmin/dataExtractSlice";
import { useAppDispatch } from "../../store/hooks";
import * as XLSX from "xlsx";
import { Helmet } from "react-helmet-async";
import { useToast } from "../../components/utilities/Toast";
import {
  AdminPageShell,
  AdminPageHeader,
} from "../../components/admin/AdminPageShell";
import { LiveRedesignReportCard } from "./LiveRedesignReportCard";
import { CustomDatePicker } from "../../components/common/DatePickers";
import { downloadDailyRedesignWorkbook } from "../../utils/dailyRedesignWorkbook";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const YEARS = [2024, 2025, 2026, 2027];

const fieldClass =
  "rounded-lg border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2 text-sm text-[#4A4A48] focus:border-[#2fa9bf] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2fa9bf]/20";

const labelClass = "mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-[#8A8A85]";

type MarkedPreset = "15d" | "20d" | "custom";

const pad = (n: number) => String(n).padStart(2, "0");
const toLocalDate = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

type ExportId =
  | "markedUnits"
  | "allData"
  | "billingData"
  | "labCases"
  | "dailyRedesign"
  | "liveProductivity";
type LabKind = "both" | "live" | "redesign";

const LAB_REDESIGN_TYPES = ["Minor", "Major", "New Design"] as const;

const LAB_REDESIGN_CATEGORIES = [
  "Query",
  "Design Preference",
  "Note Missed",
  "Design Mistake",
  "Files Mistake",
  "Client Preference Missed",
  "Need to Improve Design Quality",
  "Others (note required)",
];

const toggleValue = (current: string[], value: string) =>
  current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
type ExportConfig = {
  id: ExportId;
  title: string;
  description: string;
  includes: string;
  accent: string;
  accentLight: string;
};

const LIVE_PRODUCTIVITY_ROWS: {
  key: keyof Pick<
    LiveProductivityReport,
    | "live_cases"
    | "rd_cases"
    | "live_design_units"
    | "live_qc_units"
    | "rd_design_units"
    | "rd_qc_units"
    | "live_delivery_units"
    | "rd_delivery_units"
  >;
  label: string;
}[] = [
  { key: "live_cases", label: "total Live" },
  { key: "rd_cases", label: "total Redesigns" },
  { key: "live_design_units", label: "total Live Design units" },
  { key: "live_qc_units", label: "total Live QC units" },
  { key: "live_delivery_units", label: "total Live delivery units" },
  { key: "rd_design_units", label: "total Redesign Design units" },
  { key: "rd_qc_units", label: "total Redesign QC units" },
  { key: "rd_delivery_units", label: "total Redesign delivery units" },
];

const LIVE_PROD_PREVIEW_METRICS: {
  key: keyof Pick<
    LiveProductivityReport,
    "live_cases" | "rd_cases" | "live_design_units" | "live_delivery_units"
  >;
  label: string;
}[] = [
  { key: "live_cases", label: "Total live cases" },
  { key: "rd_cases", label: "Total redesign cases" },
  { key: "live_design_units", label: "Total live design units" },
  { key: "live_delivery_units", label: "Total live delivery units" },
];

const LiveProductivitySummary = ({
  report,
  loading,
}: {
  report: LiveProductivityReport | null;
  loading: boolean;
}) => {
  const marked = report?.live_units_all_marked;
  const unmarkedCount = report?.unmarked_live_case_ids?.length || 0;

  return (
    <div>
      <p className={labelClass}>Summary</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-2">
        {LIVE_PROD_PREVIEW_METRICS.map((metric) => (
          <div
            key={metric.key}
            className="rounded-xl bg-[#F8F8F6] px-3 py-2.5"
          >
            <p className="text-[11px] leading-snug text-[#8A8A85]">{metric.label}</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-[#4A4A48]">
              {loading || !report ? "—" : Number(report[metric.key] || 0)}
            </p>
          </div>
        ))}
        <div className="rounded-xl bg-[#F8F8F6] px-3 py-2.5">
          <p className="text-[11px] leading-snug text-[#8A8A85]">All units marked</p>
          <p
            className={`mt-1 text-lg font-semibold ${
              loading || !report
                ? "text-[#4A4A48]"
                : marked
                  ? "text-emerald-700"
                  : "text-amber-700"
            }`}
          >
            {loading || !report
              ? "—"
              : marked
                ? "Yes"
                : unmarkedCount
                  ? `No (${unmarkedCount})`
                  : "No"}
          </p>
        </div>
      </div>
    </div>
  );
};

const formatPct = (value: number | null | undefined) =>
  value == null ? "—" : `${value}%`;

const formatLabRdPct = (value: number | null | undefined) =>
  value == null ? "0.0%" : `${Number(value).toFixed(1)}%`;

const ymd = (day: number, month: number, year: number) =>
  `${year}-${pad(month)}-${pad(day)}`;

const LIVE_EMPLOYEE_GROUP_HEADER = [
  "Employee",
  "LIVE",
  "",
  "",
  "REDESIGN",
  "",
  "",
  "TOTAL",
  "",
  "",
  "FZ Unit",
  "total Live",
];
const LIVE_EMPLOYEE_COLUMN_HEADER = [
  "Employee",
  "Design units",
  "QC units",
  "Live cases",
  "Design units",
  "QC units",
  "RD cases",
  "Design RD+Live",
  "QC RD+Live",
  "",
  "",
  "",
];

const liveProductivityEmployeeRow = (row: LiveProductivityReport["employees"][number]) => {
  const designLive = Number(row.design_unit_live || 0);
  const qcLive = Number(row.qc_unit_live || 0);
  const designRd = Number(row.design_unit_redesign || 0);
  const qcRd = Number(row.qc_unit_redesign || 0);
  return [
    row.emp_name,
    designLive,
    qcLive,
    Number(row.live_cases || 0),
    designRd,
    qcRd,
    Number(row.rd_cases || 0),
    designLive + designRd,
    qcLive + qcRd,
  ];
};

const buildLiveProductivityEmployeesSheet = (
  employees: LiveProductivityReport["employees"],
  liveDeliveryUnits: number,
) => {
  const body = (employees || []).map((row) => [
    ...liveProductivityEmployeeRow(row),
    "",
    null,
  ]);
  const sumCol = (index: number) =>
    body.reduce((sum, row) => sum + Number(row[index] || 0), 0);
  const sheet = XLSX.utils.aoa_to_sheet([
    LIVE_EMPLOYEE_GROUP_HEADER,
    LIVE_EMPLOYEE_COLUMN_HEADER,
    ...body,
    [
      "Total",
      sumCol(1),
      sumCol(2),
      sumCol(3),
      sumCol(4),
      sumCol(5),
      sumCol(6),
      sumCol(7),
      sumCol(8),
      "",
      "",
      "",
    ],
    [],
    [],
    ["delivery_unit", liveDeliveryUnits, "total_fz_unit", null, "total_units", null],
  ]);
  const firstEmpExcelRow = 3;
  const lastEmpExcelRow = 2 + body.length;
  const totalExcelRow = lastEmpExcelRow + 1;
  const deliveryExcelRow = totalExcelRow + 3;
  body.forEach((row, index) => {
    const excelRow = firstEmpExcelRow + index;
    sheet[`L${excelRow}`] = {
      t: "n",
      f: `B${excelRow}+K${excelRow}`,
      v: Number(row[1] || 0),
    };
  });
  if (body.length > 0) {
    sheet[`K${totalExcelRow}`] = {
      t: "n",
      f: `SUM(K${firstEmpExcelRow}:K${lastEmpExcelRow})`,
      v: 0,
    };
    sheet[`L${totalExcelRow}`] = {
      t: "n",
      f: `SUM(L${firstEmpExcelRow}:L${lastEmpExcelRow})`,
      v: sumCol(1),
    };
    sheet[`D${deliveryExcelRow}`] = {
      t: "n",
      f: `K${totalExcelRow}`,
      v: 0,
    };
  } else {
    sheet[`D${deliveryExcelRow}`] = { t: "n", v: 0 };
  }
  sheet[`F${deliveryExcelRow}`] = {
    t: "n",
    f: `B${deliveryExcelRow}+D${deliveryExcelRow}`,
    v: liveDeliveryUnits,
  };
  sheet["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
    { s: { r: 0, c: 1 }, e: { r: 0, c: 3 } },
    { s: { r: 0, c: 4 }, e: { r: 0, c: 6 } },
    { s: { r: 0, c: 7 }, e: { r: 0, c: 8 } },
    { s: { r: 0, c: 10 }, e: { r: 1, c: 10 } },
    { s: { r: 0, c: 11 }, e: { r: 1, c: 11 } },
  ];
  sheet["!cols"] = [
    { wch: 22 },
    { wch: 14 },
    { wch: 12 },
    { wch: 12 },
    { wch: 14 },
    { wch: 12 },
    { wch: 12 },
    { wch: 16 },
    { wch: 14 },
    { wch: 3 },
    { wch: 12 },
    { wch: 12 },
  ];
  return sheet;
};

const buildRdPercentageLabsSheet = (report: RdPercentageReport) => {
  const labs = report.labs || [];
  const totalCases = labs.reduce((sum, row) => sum + Number(row.total_cases || 0), 0);
  const totalRedesigns = labs.reduce(
    (sum, row) => sum + Number(row.total_redesigns || 0),
    0,
  );
  const totalPct =
    totalCases > 0
      ? Math.round((totalRedesigns / totalCases) * 1000) / 10
      : null;
  const sheet = XLSX.utils.aoa_to_sheet([
    ["REDESIGN", "", "", ""],
    ["Lab name", "Total cases", "Total redesign", "RD %"],
    ...labs.map((row) => [
      row.lab,
      Number(row.total_cases || 0),
      Number(row.total_redesigns || 0),
      formatLabRdPct(row.rd_pct),
    ]),
    [
      "Total",
      totalCases,
      totalRedesigns,
      formatLabRdPct(totalPct),
    ],
  ]);
  sheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];
  sheet["!cols"] = [{ wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 12 }];
  return sheet;
};

const MARKED_UNITS_COLUMNS: { key: string; label: string }[] = [
  { key: "case_id", label: "Case ID" },
  { key: "creation_time", label: "Creation Time" },
  { key: "delivery_units", label: "Delivery Units" },
  { key: "designer", label: "Designer" },
  { key: "qc2", label: "QC2" },
  { key: "crm_uploader", label: "CRM Uploader" },
  { key: "units_by", label: "units_by" },
];

const BILLING_DATA_COLUMNS: { key: string; label: string }[] = [
  { key: "case_id", label: "case_id" },
  { key: "creation_time", label: "creation_time" },
  { key: "tooth_numbers", label: "tooth_numbers" },
  { key: "billing_category", label: "billing category" },
  { key: "delivery_unit", label: "delivery_unit" },
  { key: "model", label: "model" },
];

const EXPORT_CONFIGS: ExportConfig[] = [
  {
    id: "markedUnits",
    title: "Marked Units",
    description:
      "Cases whose delivery units are marked, with designer, QC2, CRM uploader, and who marked units. Filter live, redesign, or both.",
    includes:
      "Case ID, creation time (IST), delivery units, designer, QC2, CRM uploader, units_by, plus a last-row units total",
    accent: "#0f766e",
    accentLight: "#ccfbf1",
  },
  {
    id: "allData",
    title: "Monthly Lab Summary",
    description: "Token-level rollup of live cases and redesigns for the selected month.",
    includes: "Lab token, total cases, total redesigns",
    accent: "#2fa9bf",
    accentLight: "#e6f7fa",
  },
  {
    id: "billingData",
    title: "Billing Data",
    description:
      "All live cases that came in the selected month, with case creation time, delivery tooth numbers, billing category, delivery units, and client-facing models.",
    includes:
      "case_id, creation_time (IST, DD/MM/YYYY HH:MM AM/PM), tooth_numbers (delivery TN), billing category, delivery_unit, model names (pre-op and gingiva excluded)",
    accent: "#b45309",
    accentLight: "#fef3c7",
  },
  {
    id: "labCases",
    title: "Lab Cases",
    description:
      "Live cases and redesigns for one lab, with upload trail and team allocation.",
    includes:
      "Live: case ID, creation (IST), upload by/time, designer, captain. Redesign: RD case ID, redesign time (IST), category, type, note, upload by/time, designer, captain",
    accent: "#0ea5e9",
    accentLight: "#e0f2fe",
  },
  {
    id: "dailyRedesign",
    title: "Daily Redesign (12PM–12PM IST)",
    description:
      "Internal live and internal redesigns are I. Client-sent redesigns are E. Created in the 12 PM to next-day 12 PM IST window. QC ON QC is who uploaded that redesign’s files; Original QC on QC is who uploaded the previous case (live for RD-1, prior RD for RD-2+). QC CAPT, Allocated to, and Unit stay on the row even when that work is finished after the window.",
    includes:
      "Date, Lab Name, Case Name, Allocated to, QC CAPT, QC ON QC (uploader of this RD), Unit, Grade, CATEGORY, Original Designer, Original QC, Original QC on QC (uploader of prior case), Internal / External, RD No, Discussion, Year-Month, Category Designer, Notes",
    accent: "#c2410c",
    accentLight: "#ffedd5",
  },
  {
    id: "liveProductivity",
    title: "Live + Productivity (12PM–12PM IST)",
    description:
      "Live and redesign cases that came in the 12 PM to next-day 12 PM IST window, with Design+Model and QC units on those cases, plus who designed, QC2, and marked units.",
    includes:
      "Summary totals including live/RD delivery units, per-case Kind/design/QC/delivery units with designed, QC2, unit_by, and a LIVE / REDESIGN / TOTAL employee sheet with the same live delivery units below the table",
    accent: "#4338ca",
    accentLight: "#e0e7ff",
  },
];

const DownloadIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
  </svg>
);

type ExportCardProps = {
  config: ExportConfig;
  isLoading: boolean;
  onDownload: () => void;
  children: React.ReactNode;
  className?: string;
  summary?: React.ReactNode;
};

const ExportCard = ({
  config,
  isLoading,
  onDownload,
  children,
  className,
  summary,
}: ExportCardProps) => (
  <article
    className={`flex min-h-[220px] flex-col overflow-hidden rounded-2xl border border-[#E8E8E4] bg-white shadow-sm transition hover:shadow-md ${className || ""}`}
  >
    <div
      className="border-b px-5 py-4"
      style={{ backgroundColor: config.accentLight, borderColor: `${config.accent}20` }}
    >
      <div className="flex items-start gap-3">
        <span
          className="mt-1 h-9 w-1 shrink-0 rounded-full"
          style={{ backgroundColor: config.accent }}
        />
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-[#4A4A48]">{config.title}</h2>
          <p className="mt-1 text-sm leading-relaxed text-[#8A8A85]">{config.description}</p>
        </div>
      </div>
    </div>

    <div className={`flex flex-1 ${summary ? "flex-col lg:flex-row" : "flex-col"}`}>
      <div className="flex flex-1 flex-col px-5 py-4">
        <p className="mb-3 text-xs leading-relaxed text-[#8A8A85]">
          <span className="font-medium text-[#6B6B66]">Includes: </span>
          {config.includes}
        </p>

        <div className="mb-4">
          <p className={labelClass}>Filters</p>
          {children}
        </div>

        <button
          type="button"
          onClick={onDownload}
          disabled={isLoading}
          className={`mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70 bg-[#2fa9bf] hover:bg-[#2296aa] ${
            summary ? "lg:max-w-xs" : ""
          }`}
        >
          {isLoading ? (
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            <DownloadIcon />
          )}
          {isLoading ? "Exporting…" : "Download Excel"}
        </button>
      </div>

      {summary ? (
        <div className="border-t border-[#E8E8E4] px-5 py-4 lg:w-[52%] lg:border-l lg:border-t-0">
          {summary}
        </div>
      ) : null}
    </div>
  </article>
);

const DayMonthYearFilter = ({
  day,
  month,
  year,
  onDay,
  onMonth,
  onYear,
}: {
  day: number;
  month: number;
  year: number;
  onDay: (value: number) => void;
  onMonth: (value: number) => void;
  onYear: (value: number) => void;
}) => (
  <div className="flex flex-wrap gap-2">
    <select
      value={day}
      onChange={(e) => onDay(Number(e.target.value))}
      className={fieldClass}
      aria-label="Day"
    >
      {[...Array(31)].map((_, i) => (
        <option key={i + 1} value={i + 1}>{i + 1}</option>
      ))}
    </select>
    <select
      value={month}
      onChange={(e) => onMonth(Number(e.target.value))}
      className={fieldClass}
      aria-label="Month"
    >
      {MONTHS.map((name, i) => (
        <option key={name} value={i + 1}>{name}</option>
      ))}
    </select>
    <select
      value={year}
      onChange={(e) => onYear(Number(e.target.value))}
      className={fieldClass}
      aria-label="Year"
    >
      {YEARS.map((y) => (
        <option key={y} value={y}>{y}</option>
      ))}
    </select>
  </div>
);

const RateBar = ({
  label,
  value,
  tone,
}: {
  label?: string;
  value: number | null;
  tone: string;
}) => (
  <div className="space-y-1.5">
    {label ? (
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-[#6B6B66]">{label}</span>
        <span className="tabular-nums font-medium text-[#4A4A48]">{formatPct(value)}</span>
      </div>
    ) : null}
    <div className="h-2 overflow-hidden rounded-full bg-[#F0F0EC]">
      <div
        className="h-2 rounded-full transition-all"
        style={{
          width: `${Math.min(Math.max(value ?? 0, 0), 100)}%`,
          backgroundColor: tone,
        }}
      />
    </div>
  </div>
);

const RdPercentageDashboard = ({
  date,
  day,
  month,
  year,
  onDay,
  onMonth,
  onYear,
  report,
  loading,
  exporting,
  onDownload,
}: {
  date: string;
  day: number;
  month: number;
  year: number;
  onDay: (value: number) => void;
  onMonth: (value: number) => void;
  onYear: (value: number) => void;
  report: RdPercentageReport | null;
  loading: boolean;
  exporting: boolean;
  onDownload: () => void;
}) => {
  const counts = report?.counts;
  const pct = report?.percentages;
  const labs = report?.labs || [];
  const labCases = labs.reduce((sum, row) => sum + Number(row.total_cases || 0), 0);
  const labRedesigns = labs.reduce(
    (sum, row) => sum + Number(row.total_redesigns || 0),
    0,
  );
  const labPct = labCases ? Math.round((labRedesigns / labCases) * 1000) / 10 : null;
  const rates = [
    {
      label: "Total redesigns / Live",
      value: pct?.total_rd_of_live ?? null,
      hint: loading
        ? "—"
        : `${counts?.total_redesigns ?? 0} redesigns · ${counts?.live ?? 0} live`,
      tone: "#c2410c",
    },
    {
      label: "Internal redesigns / Live",
      value: pct?.internal_of_live ?? null,
      hint: loading
        ? "—"
        : `${counts?.internal ?? 0} internal · ${counts?.live ?? 0} live`,
      tone: "#8b5cf6",
    },
    {
      label: "Client redesigns / Live",
      value: pct?.client_of_live ?? null,
      hint: loading
        ? "—"
        : `${counts?.client ?? 0} client · ${counts?.live ?? 0} live`,
      tone: "#0ea5e9",
    },
  ];

  return (
    <section className="overflow-hidden rounded-2xl border border-[#E8E8E4] bg-white shadow-sm">
      <div className="border-b border-orange-100 bg-orange-50 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-[#4A4A48]">
              RD Percentage (12PM–12PM IST)
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-[#8A8A85]">
              Total, internal, and client redesigns generated vs live cases created
              in the same 12PM–12PM IST window for {date}, plus lab-wise RD %.
              Client totals only include Note Missed, Design Mistake, Files Mistake,
              and Client Preference Missed.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DayMonthYearFilter
              day={day}
              month={month}
              year={year}
              onDay={onDay}
              onMonth={onMonth}
              onYear={onYear}
            />
            <button
              type="button"
              onClick={onDownload}
              disabled={loading || exporting}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2fa9bf] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2296aa] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {exporting ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              ) : (
                <DownloadIcon />
              )}
              {exporting ? "Exporting…" : "Download Excel"}
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-5 px-5 py-5">
        <div className="grid gap-3 sm:grid-cols-3">
          {rates.map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-[#E8E8E4] bg-[#F8F8F6] px-4 py-3"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">
                {item.label}
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-[#4A4A48]">
                {loading ? "—" : formatPct(item.value)}
              </p>
              <p className="mt-1 text-[11px] text-[#8A8A85]">{item.hint}</p>
              <div className="mt-3">
                <RateBar value={loading ? null : item.value} tone={item.tone} />
              </div>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto rounded-2xl border border-[#E8E8E4]">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#F8F8F6] text-left">
                <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">
                  Lab name
                </th>
                <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">
                  Total cases
                </th>
                <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">
                  Total redesign
                </th>
                <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">
                  RD %
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && !labs.length ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-[#8A8A85]">
                    Loading lab-wise RD %…
                  </td>
                </tr>
              ) : labs.length ? (
                <>
                  {labs.map((row) => (
                    <tr key={row.lab} className="border-b border-[#F0F0EC] last:border-0">
                      <td className="px-4 py-2 font-medium text-[#4A4A48]">{row.lab}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-[#4A4A48]">
                        {Number(row.total_cases || 0)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-[#4A4A48]">
                        {Number(row.total_redesigns || 0)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-[#4A4A48]">
                        {formatLabRdPct(row.rd_pct)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-[#F8F8F6] font-semibold">
                    <td className="px-4 py-2.5 text-[#4A4A48]">Total</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[#4A4A48]">
                      {labCases}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[#4A4A48]">
                      {labRedesigns}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[#4A4A48]">
                      {formatLabRdPct(labPct)}
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-[#8A8A85]">
                    No lab-wise RD data for this window
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const DataExports = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const [monthAll, setMonthAll] = useState(new Date().getMonth() + 1);
  const [yearAll, setYearAll] = useState(new Date().getFullYear());

  const [monthBilling, setMonthBilling] = useState(new Date().getMonth() + 1);
  const [yearBilling, setYearBilling] = useState(new Date().getFullYear());

  const [labToken, setLabToken] = useState("JU");
  const [monthLab, setMonthLab] = useState(new Date().getMonth() + 1);
  const [yearLab, setYearLab] = useState(new Date().getFullYear());
  const [labKind, setLabKind] = useState<LabKind>("both");
  const [labCategories, setLabCategories] = useState<string[]>([]);
  const [labTypes, setLabTypes] = useState<string[]>([]);

  const [dailyRdDate, setDailyRdDate] = useState(new Date().getDate());
  const [dailyRdMonth, setDailyRdMonth] = useState(new Date().getMonth() + 1);
  const [dailyRdYear, setDailyRdYear] = useState(new Date().getFullYear());

  const [liveProdDate, setLiveProdDate] = useState(new Date().getDate());
  const [liveProdMonth, setLiveProdMonth] = useState(new Date().getMonth() + 1);
  const [liveProdYear, setLiveProdYear] = useState(new Date().getFullYear());
  const [liveProdReport, setLiveProdReport] = useState<LiveProductivityReport | null>(null);
  const [liveProdLoading, setLiveProdLoading] = useState(false);

  const [rdPctDate, setRdPctDate] = useState(new Date().getDate());
  const [rdPctMonth, setRdPctMonth] = useState(new Date().getMonth() + 1);
  const [rdPctYear, setRdPctYear] = useState(new Date().getFullYear());
  const [rdReport, setRdReport] = useState<RdPercentageReport | null>(null);
  const [rdDashboardLoading, setRdDashboardLoading] = useState(false);
  const [rdExporting, setRdExporting] = useState(false);

  const today = toLocalDate(new Date());
  const [markedPreset, setMarkedPreset] = useState<MarkedPreset>("15d");
  const [markedFromDate, setMarkedFromDate] = useState(today);
  const [markedToDate, setMarkedToDate] = useState(today);
  const [markedKind, setMarkedKind] = useState<LabKind>("both");

  const markedRange = useMemo(() => {
    if (markedPreset === "15d") {
      return { from: toLocalDate(addDays(new Date(), -14)), to: today };
    }
    if (markedPreset === "20d") {
      return { from: toLocalDate(addDays(new Date(), -19)), to: today };
    }
    return { from: markedFromDate, to: markedToDate };
  }, [markedPreset, markedFromDate, markedToDate, today]);

  const [loadingExport, setLoadingExport] = useState<ExportId | null>(null);
  const rdPercentageDate = ymd(rdPctDate, rdPctMonth, rdPctYear);
  const dailyRedesignDate = ymd(dailyRdDate, dailyRdMonth, dailyRdYear);
  const liveProductivityDate = ymd(liveProdDate, liveProdMonth, liveProdYear);

  useEffect(() => {
    let cancelled = false;
    setRdDashboardLoading(true);
    dispatch(fetchRdPercentage({ date: rdPercentageDate }))
      .unwrap()
      .then((data) => {
        if (!cancelled) setRdReport(data);
      })
      .catch(() => {
        if (!cancelled) setRdReport(null);
      })
      .finally(() => {
        if (!cancelled) setRdDashboardLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, rdPercentageDate]);

  useEffect(() => {
    let cancelled = false;
    setLiveProdLoading(true);
    dispatch(fetchLiveProductivity({ date: liveProductivityDate }))
      .unwrap()
      .then((data) => {
        if (!cancelled) setLiveProdReport(data);
      })
      .catch(() => {
        if (!cancelled) setLiveProdReport(null);
      })
      .finally(() => {
        if (!cancelled) setLiveProdLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, liveProductivityDate]);

  const downloadExcel = (data: unknown[], fileName: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const handleExcelDownload = (data: unknown[], fileName: string) => {
    if (!data || data.length === 0) {
      showToast("No data found for the selected period", "error");
      return;
    }
    downloadExcel(data, fileName);
    showToast("Export downloaded successfully", "success");
  };

  const withLoading = async (id: ExportId, fn: () => Promise<void>) => {
    setLoadingExport(id);
    try {
      await fn();
    } finally {
      setLoadingExport(null);
    }
  };

  const loadAllData = () =>
    withLoading("allData", async () => {
      try {
        const res = await dispatch(
          fetchAllLiveAndRedesignsByMonth({ month: monthAll, year: yearAll })
        ).unwrap();
        handleExcelDownload(res, `All_Data_${monthAll}_${yearAll}`);
      } catch (error) {
        console.error(error);
        showToast("Something went wrong", "error");
      }
    });

  const loadBillingData = () =>
    withLoading("billingData", async () => {
      try {
        const rows = await dispatch(
          fetchBillingDataByMonth({ month: monthBilling, year: yearBilling }),
        ).unwrap();
        if (!rows?.length) {
          showToast("No data found for the selected period", "error");
          return;
        }
        const excelRows = rows.map((row) =>
          Object.fromEntries(
            BILLING_DATA_COLUMNS.map(({ key, label }) => [
              label,
              (row as Record<string, unknown>)[key] ?? "",
            ]),
          ),
        );
        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        worksheet["!cols"] = BILLING_DATA_COLUMNS.map((col) => ({
          wch: Math.max(col.label.length + 2, col.key === "creation_time" ? 22 : 18),
        }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Billing Data");
        XLSX.writeFile(
          workbook,
          `Billing_Data_${monthBilling}_${yearBilling}.xlsx`,
        );
        showToast("Export downloaded successfully", "success");
      } catch (error) {
        console.error(error);
        showToast("Something went wrong", "error");
      }
    });

  const loadLabExport = () =>
    withLoading("labCases", async () => {
      const lab = labToken.trim().toUpperCase();
      if (!lab) {
        showToast("Enter a lab token", "error");
        return;
      }
      try {
        const wantLive = labKind !== "redesign";
        const wantRedesign = labKind !== "live";
        const [liveRows, redesignRows] = await Promise.all([
          wantLive
            ? dispatch(
                fetchLabWiseCasesByMonth({ lab, month: monthLab, year: yearLab }),
              ).unwrap()
            : Promise.resolve([] as unknown[]),
          wantRedesign
            ? dispatch(
                fetchLabWiseRedesignsByMonth({
                  lab,
                  month: monthLab,
                  year: yearLab,
                  categories: labCategories,
                  types: labTypes,
                }),
              ).unwrap()
            : Promise.resolve([] as unknown[]),
        ]);

        const live = Array.isArray(liveRows) ? liveRows : [];
        const redesigns = Array.isArray(redesignRows) ? redesignRows : [];

        if (labKind === "live") {
          handleExcelDownload(live, `Lab_Cases_${lab}_${monthLab}_${yearLab}`);
          return;
        }
        if (labKind === "redesign") {
          handleExcelDownload(
            redesigns,
            `Lab_Redesigns_${lab}_${monthLab}_${yearLab}`,
          );
          return;
        }

        if (!live.length && !redesigns.length) {
          showToast("No data found for the selected period", "error");
          return;
        }

        const workbook = XLSX.utils.book_new();
        if (live.length) {
          XLSX.utils.book_append_sheet(
            workbook,
            XLSX.utils.json_to_sheet(live),
            "Live",
          );
        }
        if (redesigns.length) {
          XLSX.utils.book_append_sheet(
            workbook,
            XLSX.utils.json_to_sheet(redesigns),
            "Redesign",
          );
        }
        XLSX.writeFile(workbook, `Lab_${lab}_${monthLab}_${yearLab}.xlsx`);
        showToast("Export downloaded successfully", "success");
      } catch (error) {
        console.error(error);
        showToast("Something went wrong", "error");
      }
    });

  const loadMarkedUnits = () =>
    withLoading("markedUnits", async () => {
      if (markedRange.from > markedRange.to) {
        showToast("From date cannot be after to date", "error");
        return;
      }
      try {
        const rows = await dispatch(
          fetchMarkedUnitsExport({
            fromDate: markedRange.from,
            toDate: markedRange.to,
            kind: markedKind,
          })
        ).unwrap();
        if (!rows?.length) {
          showToast("No marked units found for the selected period", "error");
          return;
        }
        const excelRows = rows.map((row) =>
          Object.fromEntries(
            MARKED_UNITS_COLUMNS.map(({ key, label }) => [label, row[key] ?? ""]),
          ),
        );
        const totalUnits = rows.reduce(
          (sum, row) => sum + Number(row.delivery_units || 0),
          0,
        );
        excelRows.push(
          Object.fromEntries(
            MARKED_UNITS_COLUMNS.map(({ key, label }) => [
              label,
              key === "case_id"
                ? "Total"
                : key === "delivery_units"
                  ? totalUnits
                  : "",
            ]),
          ),
        );
        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        worksheet["!cols"] = MARKED_UNITS_COLUMNS.map((col) => ({
          wch: Math.max(col.label.length + 2, 16),
        }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Marked Units");
        const kindSuffix = markedKind === "both" ? "" : `_${markedKind}`;
        XLSX.writeFile(
          workbook,
          `Marked_Units${kindSuffix}_${markedRange.from}_to_${markedRange.to}.xlsx`
        );
        showToast("Export downloaded successfully", "success");
      } catch (error) {
        console.error(error);
        showToast("Something went wrong", "error");
      }
    });

  const loadDailyRedesigns = () =>
    withLoading("dailyRedesign", async () => {
      try {
        const rows = await dispatch(
          fetchDailyRedesigns({ date: dailyRedesignDate }),
        ).unwrap();
        if (!rows?.length) {
          showToast("No redesigns found for the selected 12PM–12PM IST window", "error");
          return;
        }
        await downloadDailyRedesignWorkbook(
          rows,
          `Daily_Redesign_${dailyRedesignDate}.xlsx`,
        );
        showToast("Export downloaded successfully", "success");
      } catch (error) {
        console.error(error);
        showToast("Something went wrong", "error");
      }
    });

  const loadLiveProductivity = () =>
    withLoading("liveProductivity", async () => {
      try {
        const report =
          liveProdReport?.date === liveProductivityDate
            ? liveProdReport
            : await dispatch(
                fetchLiveProductivity({ date: liveProductivityDate }),
              ).unwrap();
        if (!report) {
          showToast("No Live + Productivity data for the selected window", "error");
          return;
        }
        setLiveProdReport(report);

        const summaryRows: (string | number)[][] = [];
        LIVE_PRODUCTIVITY_ROWS.forEach((row) => {
          summaryRows.push([row.label, Number(report[row.key] || 0)]);
        });
        summaryRows.push([""]);
        summaryRows.push([
          "all live cases marked",
          report.live_units_all_marked ? "True" : "False",
        ]);
        summaryRows.push([
          "unmarked live cases",
          (report.unmarked_live_case_ids || []).join(", "),
        ]);

        const caseBody = (report.cases || []).map((row) => [
          row.kind,
          row.case_id,
          Number(row.design_unit || 0),
          Number(row.qc_unit || 0),
          Number(row.del_unit || 0),
          row.designed || "",
          row.qc2 || "",
          row.unit_by || "",
        ]);

        const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
        summarySheet["!cols"] = [{ wch: 32 }, { wch: 48 }];

        const casesSheet = XLSX.utils.aoa_to_sheet([
          [
            "Kind",
            "case_id",
            "design_unit",
            "qc_unit",
            "delivery_units",
            "designed",
            "qc2",
            "unit_by",
          ],
          ...caseBody,
        ]);
        casesSheet["!cols"] = [
          { wch: 10 },
          { wch: 22 },
          { wch: 14 },
          { wch: 12 },
          { wch: 12 },
          { wch: 28 },
          { wch: 22 },
          { wch: 22 },
        ];

        const employeesSheet = buildLiveProductivityEmployeesSheet(
          report.employees || [],
          Number(report.live_delivery_units || 0),
        );

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
        XLSX.utils.book_append_sheet(workbook, casesSheet, "Cases");
        XLSX.utils.book_append_sheet(workbook, employeesSheet, "Employees");
        XLSX.writeFile(workbook, `Live_Productivity_${liveProductivityDate}.xlsx`);
        showToast("Export downloaded successfully", "success");
      } catch (error) {
        console.error(error);
        showToast("Something went wrong", "error");
      }
    });

  const downloadRdPercentage = async () => {
    setRdExporting(true);
    try {
      const report =
        rdReport?.date === rdPercentageDate
          ? rdReport
          : await dispatch(fetchRdPercentage({ date: rdPercentageDate })).unwrap();
      if (!report) {
        showToast("No RD percentage data for the selected window", "error");
        return;
      }
      setRdReport(report);
      const worksheet = buildRdPercentageLabsSheet(report);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "REDESIGN");
      XLSX.writeFile(workbook, `RD_Percentage_${rdPercentageDate}.xlsx`);
      showToast("Export downloaded successfully", "success");
    } catch (error) {
      console.error(error);
      showToast("Something went wrong", "error");
    } finally {
      setRdExporting(false);
    }
  };

  const downloadHandlers: Record<ExportId, () => void> = {
    markedUnits: loadMarkedUnits,
    allData: loadAllData,
    billingData: loadBillingData,
    labCases: loadLabExport,
    dailyRedesign: loadDailyRedesigns,
    liveProductivity: loadLiveProductivity,
  };

  const renderFilters = (id: ExportId) => {
    switch (id) {
      case "markedUnits":
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-1 rounded-xl bg-[#F8F8F6] p-1">
              {([
                { id: "both", label: "Both" },
                { id: "live", label: "Live" },
                { id: "redesign", label: "Redesign" },
              ] as { id: LabKind; label: string }[]).map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setMarkedKind(option.id)}
                  className={`rounded-lg px-1.5 py-1.5 text-[11px] font-medium ${
                    markedKind === option.id
                      ? "bg-white text-[#2fa9bf] shadow-sm"
                      : "text-[#6B6B66] hover:text-[#4A4A48]"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-1 rounded-xl bg-[#F8F8F6] p-1">
              {([
                { id: "15d", label: "15 days" },
                { id: "20d", label: "20 days" },
                { id: "custom", label: "Custom" },
              ] as { id: MarkedPreset; label: string }[]).map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => setMarkedPreset(preset.id)}
                  className={`rounded-lg px-1.5 py-1.5 text-[11px] font-medium ${
                    markedPreset === preset.id
                      ? "bg-white text-[#2fa9bf] shadow-sm"
                      : "text-[#6B6B66] hover:text-[#4A4A48]"
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            {markedPreset === "custom" ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <CustomDatePicker
                  label="From"
                  value={markedFromDate}
                  max={markedToDate}
                  onChange={setMarkedFromDate}
                />
                <CustomDatePicker
                  label="To"
                  value={markedToDate}
                  min={markedFromDate}
                  onChange={setMarkedToDate}
                />
              </div>
            ) : (
              <p className="text-[11px] text-[#8A8A85]">
                {markedRange.from === markedRange.to
                  ? markedRange.from
                  : `${markedRange.from} to ${markedRange.to}`}
              </p>
            )}
          </div>
        );
      case "allData":
        return (
          <div className="flex flex-wrap gap-2">
            <select
              value={monthAll}
              onChange={(e) => setMonthAll(Number(e.target.value))}
              className={fieldClass}
              aria-label="Month"
            >
              {MONTHS.map((name, i) => (
                <option key={name} value={i + 1}>{name}</option>
              ))}
            </select>
            <select
              value={yearAll}
              onChange={(e) => setYearAll(Number(e.target.value))}
              className={fieldClass}
              aria-label="Year"
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        );
      case "billingData":
        return (
          <div className="flex flex-wrap gap-2">
            <select
              value={monthBilling}
              onChange={(e) => setMonthBilling(Number(e.target.value))}
              className={fieldClass}
              aria-label="Month"
            >
              {MONTHS.map((name, i) => (
                <option key={name} value={i + 1}>{name}</option>
              ))}
            </select>
            <select
              value={yearBilling}
              onChange={(e) => setYearBilling(Number(e.target.value))}
              className={fieldClass}
              aria-label="Year"
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        );
      case "labCases":
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-1 rounded-xl bg-[#F8F8F6] p-1">
              {([
                { id: "both", label: "Both" },
                { id: "live", label: "Live" },
                { id: "redesign", label: "Redesign" },
              ] as { id: LabKind; label: string }[]).map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setLabKind(option.id)}
                  className={`rounded-lg px-1.5 py-1.5 text-[11px] font-medium ${
                    labKind === option.id
                      ? "bg-white text-[#2fa9bf] shadow-sm"
                      : "text-[#6B6B66] hover:text-[#4A4A48]"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                value={labToken}
                onChange={(e) => setLabToken(e.target.value.toUpperCase())}
                placeholder="Lab"
                className={`${fieldClass} w-16`}
                aria-label="Lab token"
              />
              <select
                value={monthLab}
                onChange={(e) => setMonthLab(Number(e.target.value))}
                className={fieldClass}
                aria-label="Month"
              >
                {MONTHS.map((name, i) => (
                  <option key={name} value={i + 1}>{name}</option>
                ))}
              </select>
              <select
                value={yearLab}
                onChange={(e) => setYearLab(Number(e.target.value))}
                className={fieldClass}
                aria-label="Year"
              >
                {YEARS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            {labKind !== "live" ? (
              <div className="space-y-2">
                <div>
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-[#8A8A85]">
                      Category
                    </p>
                    {labCategories.length ? (
                      <button
                        type="button"
                        onClick={() => setLabCategories([])}
                        className="text-[11px] font-medium text-[#2fa9bf]"
                      >
                        Clear
                      </button>
                    ) : (
                      <span className="text-[11px] text-[#8A8A85]">All</span>
                    )}
                  </div>
                  <div className="grid max-h-40 grid-cols-2 gap-x-2 gap-y-1 overflow-y-auto rounded-xl border border-[#E8E8E4] bg-[#F8F8F6] p-2">
                    {LAB_REDESIGN_CATEGORIES.map((name) => (
                      <label
                        key={name}
                        className="flex cursor-pointer items-start gap-1.5 text-xs leading-snug text-[#4A4A48]"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={labCategories.includes(name)}
                          onChange={() =>
                            setLabCategories((current) => toggleValue(current, name))
                          }
                        />
                        <span>{name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-[#8A8A85]">
                      Type
                    </p>
                    {labTypes.length ? (
                      <button
                        type="button"
                        onClick={() => setLabTypes([])}
                        className="text-[11px] font-medium text-[#2fa9bf]"
                      >
                        Clear
                      </button>
                    ) : (
                      <span className="text-[11px] text-[#8A8A85]">All</span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-1 rounded-xl bg-[#F8F8F6] p-1">
                    {LAB_REDESIGN_TYPES.map((name) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() =>
                          setLabTypes((current) => toggleValue(current, name))
                        }
                        className={`rounded-lg px-1.5 py-1.5 text-[11px] font-medium ${
                          labTypes.includes(name)
                            ? "bg-white text-[#2fa9bf] shadow-sm"
                            : "text-[#6B6B66] hover:text-[#4A4A48]"
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] leading-relaxed text-[#8A8A85]">
                  Applies to redesigns. Leave a group on All to skip that filter.
                </p>
              </div>
            ) : null}
          </div>
        );
      case "dailyRedesign":
        return (
          <DayMonthYearFilter
            day={dailyRdDate}
            month={dailyRdMonth}
            year={dailyRdYear}
            onDay={setDailyRdDate}
            onMonth={setDailyRdMonth}
            onYear={setDailyRdYear}
          />
        );
      case "liveProductivity":
        return (
          <DayMonthYearFilter
            day={liveProdDate}
            month={liveProdMonth}
            year={liveProdYear}
            onDay={setLiveProdDate}
            onMonth={setLiveProdMonth}
            onYear={setLiveProdYear}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Export Data</title>
      </Helmet>

      <AdminPageShell>
        <AdminPageHeader
          title="Data Exports"
          subtitle="Download Excel reports for marked units, cases, redesigns, and operational-day reports"
        />

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {EXPORT_CONFIGS.map((config) => (
            <ExportCard
              key={config.id}
              config={config}
              isLoading={loadingExport === config.id}
              onDownload={downloadHandlers[config.id]}
              className={
                config.id === "liveProductivity" ? "sm:col-span-2 lg:col-span-2" : undefined
              }
              summary={
                config.id === "liveProductivity" ? (
                  <LiveProductivitySummary
                    report={liveProdReport}
                    loading={liveProdLoading}
                  />
                ) : undefined
              }
            >
              {renderFilters(config.id)}
            </ExportCard>
          ))}
        </div>

        <RdPercentageDashboard
          date={rdPercentageDate}
          day={rdPctDate}
          month={rdPctMonth}
          year={rdPctYear}
          onDay={setRdPctDate}
          onMonth={setRdPctMonth}
          onYear={setRdPctYear}
          report={rdReport}
          loading={rdDashboardLoading}
          exporting={rdExporting}
          onDownload={downloadRdPercentage}
        />

        <LiveRedesignReportCard />
      </AdminPageShell>
    </>
  );
};

export default DataExports;
